import type {
  RatingDistributionDto,
  ReputationOverviewDto,
} from '@up2stay/up2promo-api-client';
import { Button, Card, CardContent, Skeleton, cn } from '@up2stay/up2promo-ui';
import { ArrowRight, Star } from 'lucide-react';

import { formatReputationScoreValue } from '@/core/components/reputation/reputation-score-formula';
import { shellPaths } from '@/core/router/app-paths';

import {
  statusBoardCardClass,
  statusBoardCardPadClass,
  statusBoardSectionTitleClass,
} from './dashboard-board-surfaces';

const STAR_COLORS: Record<1 | 2 | 3 | 4 | 5, string> = {
  5: 'var(--ok)',
  4: '#65a30d',
  3: '#ca8a04',
  2: '#ea580c',
  1: 'var(--destructive)',
};

function ScoreRing({
  score,
  display,
  max = 100,
  size = 104,
  label,
  ofLabel,
}: {
  score: number;
  display: string;
  max?: number;
  size?: number;
  label: string;
  ofLabel: string;
}) {
  const r = 44;
  const c = 2 * Math.PI * r;
  const pct = Math.min(1, Math.max(0, score / max));
  const dash = `${(c * pct).toFixed(1)} ${c.toFixed(1)}`;

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      aria-label={`${label} ${display} ${ofLabel}`}
    >
      <svg width={size} height={size} viewBox="0 0 104 104" aria-hidden>
        <circle
          cx="52"
          cy="52"
          r={r}
          fill="none"
          stroke="var(--border)"
          strokeWidth="10"
        />
        <circle
          cx="52"
          cy="52"
          r={r}
          fill="none"
          stroke="var(--brand-500)"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={dash}
          transform="rotate(-90 52 52)"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[28px] font-bold leading-none tracking-tight text-foreground md:text-[30px]">
          {display}
        </span>
        <span className="mt-0.5 text-[10px] text-muted-foreground md:text-[11px]">
          {ofLabel}
        </span>
      </div>
    </div>
  );
}

/** Gold stars — Status Board comps use #E0A21B, not sentiment green. */
function BoardStarRating({ rating }: { rating: number }) {
  const value = Math.min(5, Math.max(0, Math.round(rating)));
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`${value} / 5`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            'h-3.5 w-3.5',
            i < value
              ? 'fill-[#e0a21b] text-[#e0a21b]'
              : 'fill-muted text-muted-foreground/35',
          )}
        />
      ))}
    </span>
  );
}

function DistributionBars({
  distribution,
  total,
}: {
  distribution: RatingDistributionDto;
  total: number;
}) {
  const rows = ([5, 4, 3, 2, 1] as const).map((n) => {
    const count = distribution[n] ?? 0;
    const pct =
      total > 0
        ? Math.max(count > 0 ? 2 : 0, Math.round((count / total) * 100))
        : 0;
    return { n, count, pct };
  });

  return (
    <div className="flex flex-col gap-[7px]">
      {rows.map((row) => (
        <div key={row.n} className="flex items-center gap-2.5 text-[12px]">
          <span className="w-[22px] font-mono tabular-nums text-muted-foreground">
            {row.n}★
          </span>
          <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
            <div
              className="h-2 rounded transition-[width]"
              style={{
                width: `${row.pct}%`,
                background: STAR_COLORS[row.n],
              }}
            />
          </div>
          <span className="w-[22px] text-right font-mono tabular-nums text-foreground">
            {row.count}
          </span>
        </div>
      ))}
    </div>
  );
}

export function DashboardReputationCard({
  overview,
  reputationEnabled,
  isLoading,
  isMobile,
  onOpen,
  labels,
}: {
  overview: ReputationOverviewDto | undefined;
  reputationEnabled: boolean;
  isLoading: boolean;
  isMobile: boolean;
  onOpen: (href: string) => void;
  labels: {
    title: string;
    openAll: string;
    openAllDesktop: string;
    score: string;
    of100: string;
    reviewsCount: string;
    responseRate: string;
    responseRateWarn: string;
    empty: string;
  };
}) {
  const scoreValue = overview?.score.score ?? 0;
  const scoreDisplay = overview
    ? formatReputationScoreValue(overview.score)
    : '0';

  if (isLoading) {
    return (
      <Card className={cn(statusBoardCardClass, 'h-full')} aria-busy="true">
        <CardContent className={cn('space-y-4', statusBoardCardPadClass)}>
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-24 w-full rounded-md" />
        </CardContent>
      </Card>
    );
  }

  const empty = !reputationEnabled || !overview || overview.totalReviews === 0;
  const responsePct = overview
    ? Math.round((overview.responseRate ?? 0) * 100)
    : 0;
  const lowResponse = !empty && responsePct < 40;

  return (
    <section
      aria-labelledby="dashboard-reputation-heading"
      className="flex h-full min-w-0 flex-col"
    >
      <Card className={cn(statusBoardCardClass, 'h-full')}>
        <CardContent
          className={cn('flex flex-col gap-4', statusBoardCardPadClass)}
        >
          <div className="flex items-center gap-2">
            <h2
              id="dashboard-reputation-heading"
              className={cn('min-w-0 flex-1', statusBoardSectionTitleClass)}
            >
              {labels.title}
            </h2>
            <Button
              type="button"
              variant="ghostAccent"
              size="sm"
              className="h-8 px-2 text-[13px] font-medium"
              onClick={() => onOpen(shellPaths.reputationDashboard)}
            >
              {isMobile ? labels.openAll : labels.openAllDesktop}
            </Button>
          </div>

          {empty ? (
            <div className="flex flex-col gap-3">
              <p className="text-[13.5px] text-muted-foreground">
                {labels.empty}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11 w-fit"
                onClick={() => onOpen(shellPaths.reputationSetup)}
              >
                {isMobile ? labels.openAll : labels.openAllDesktop}
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4 md:gap-[18px]">
                <ScoreRing
                  score={scoreValue}
                  display={scoreDisplay}
                  size={isMobile ? 88 : 104}
                  label={labels.score}
                  ofLabel={labels.of100}
                />
                <div className="flex min-w-0 flex-col gap-1.5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[26px] font-bold tracking-tight text-foreground md:text-[28px]">
                      {overview!.averageRating.toFixed(1).replace('.', ',')}
                    </span>
                    <BoardStarRating rating={overview!.averageRating} />
                  </div>
                  <span className="text-[13px] text-muted-foreground">
                    {labels.reviewsCount.replace(
                      '{count}',
                      String(overview!.totalReviews),
                    )}
                  </span>
                  {lowResponse ? (
                    <span className="mt-0.5 inline-flex self-start rounded-full bg-[#fdebdd] px-2.5 py-1 text-[12px] font-semibold text-[#9a3412] dark:bg-amber-500/15 dark:text-amber-300">
                      {labels.responseRateWarn.replace(
                        '{pct}',
                        String(responsePct),
                      )}
                    </span>
                  ) : (
                    <span className="text-[12px] text-muted-foreground">
                      {labels.responseRate.replace(
                        '{pct}',
                        String(responsePct),
                      )}
                    </span>
                  )}
                </div>
              </div>

              <DistributionBars
                distribution={overview!.ratingDistribution}
                total={overview!.totalReviews}
              />
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
